import type { EmailT, EmailImageT, EmailSectionT } from "~/types"
import { normalizeSections, sectionCells } from "~/composables/useEmailSections"

type SliceFile = {
  key: string
  filename: string
  size: number
  lastModified?: string | Date
}

type ListResponse = {
  success: boolean
  prefix: string
  files: SliceFile[]
}

type DetectedSlice = {
  number: number
  letter: string
  ext: string
  key: string // composite, e.g. "4" or "4a"
}

// Slice files may carry an optional cell letter from vertical splitting. Both the new
// underscore-separated form (desktop_04_a.jpg) and the legacy glued form (desktop_04a.jpg)
// are accepted; the letter is captured without the separator so keys stay stable.
const SLICE_FILENAME_REGEX = /^(desktop|mobile)_(\d+)(?:_?([a-z]))?\.(jpg|jpeg|png|gif|webp)$/i
const SLICE_SRC_REGEX = /(?:\$SIZE|desktop|mobile)_(\d+)(?:_?([a-z]))?\.(jpg|jpeg|png|gif|webp)/i

function sliceKey(number: number, letter: string): string {
  return `${number}${letter.toLowerCase()}`
}

function extractSliceNumber(src: string): number | null {
  const m = SLICE_SRC_REGEX.exec(src)
  if (!m) return null
  return parseInt(m[1]!, 10)
}

function extractSliceKey(src: string): string | null {
  const m = SLICE_SRC_REGEX.exec(src)
  if (!m) return null
  return sliceKey(parseInt(m[1]!, 10), m[2] || "")
}

/** Read the cells of a section as a mutable array (tolerant of the legacy `image` field). */
function getCells(section: EmailSectionT): EmailImageT[] {
  return sectionCells(section)
}

function setCells(section: EmailSectionT, cells: EmailImageT[]) {
  section.type = "image"
  section.cells = cells
  delete section.image
}

export function useEmailSlices(groupAlias: string, emailAlias: string) {
  const availableSlices = ref<DetectedSlice[]>([])
  const sizeCounts = ref<{ desktop: number; mobile: number }>({ desktop: 0, mobile: 0 })
  // filename → object mtime (epoch seconds). Re-slicing overwrites slices under the SAME
  // filename, and the CDN serves them with a 4h TTL, so a bare URL keeps showing the old
  // image. Callers append this as a `?v=` cache key: it changes only when the file actually
  // changes, so thumbnails refresh on re-slice without re-downloading on every render.
  const sliceVersions = ref<Record<string, number>>({})
  const loading = ref(false)
  const loaded = ref(false)
  const lastError = ref<string | null>(null)

  async function fetchSlices() {
    loading.value = true
    lastError.value = null
    try {
      const res = await $fetch<ListResponse>(`/api/v1/images/${groupAlias}/${emailAlias}/list`)
      const map = new Map<string, DetectedSlice>()
      const counts = { desktop: 0, mobile: 0 }
      const versions: Record<string, number> = {}
      for (const file of res.files) {
        const m = SLICE_FILENAME_REGEX.exec(file.filename)
        if (!m) continue
        const size = m[1]!.toLowerCase() as "desktop" | "mobile"
        const number = parseInt(m[2]!, 10)
        const letter = (m[3] || "").toLowerCase()
        const ext = m[4]!.toLowerCase()
        counts[size] = (counts[size] || 0) + 1
        const mtime = file.lastModified ? new Date(file.lastModified).getTime() : NaN
        if (!Number.isNaN(mtime)) versions[file.filename] = Math.floor(mtime / 1000)
        const key = sliceKey(number, letter)
        if (!map.has(key)) map.set(key, { number, letter, ext, key })
      }
      sizeCounts.value = counts
      sliceVersions.value = versions
      availableSlices.value = Array.from(map.values()).sort(
        (a, b) => a.number - b.number || a.letter.localeCompare(b.letter),
      )
      loaded.value = true
    } catch (err) {
      lastError.value = err instanceof Error ? err.message : "Failed to list slices"
      availableSlices.value = []
      sliceVersions.value = {}
    } finally {
      loading.value = false
    }
  }

  /**
   * Cache-busting token for a slice filename, or undefined when the file isn't in the last
   * listing (e.g. a legacy glued-letter name the email references but storage doesn't hold).
   * Callers fall back to the unversioned URL in that case.
   */
  function sliceVersion(filename: string): number | undefined {
    return sliceVersions.value[filename]
  }

  function getExistingSliceKeys(email: EmailT | null | undefined): Set<string> {
    const out = new Set<string>()
    if (!email?.sections) return out
    for (const section of normalizeSections(email.sections)) {
      for (const cell of getCells(section)) {
        const k = extractSliceKey(cell.src || "")
        if (k != null) out.add(k)
      }
    }
    return out
  }

  /** Detected slices not yet referenced anywhere in the email. */
  function missingSlices(email: EmailT | null | undefined): DetectedSlice[] {
    const existing = getExistingSliceKeys(email)
    return availableSlices.value
      .filter((s) => !existing.has(s.key))
      .sort((a, b) => a.number - b.number || a.letter.localeCompare(b.letter))
  }

  /** Backward-compatible numeric view of the missing slices. */
  function missingSliceNumbers(email: EmailT | null | undefined): number[] {
    return missingSlices(email).map((s) => s.number)
  }

  // Index in `sections` at which to splice a NEW image band for slice number `n`, keeping
  // image bands ordered by slice number (a new band goes before the first band whose
  // numbers exceed `n`, else right after the last image band).
  function imageBandInsertIndex(sections: EmailSectionT[], n: number): number {
    let afterIdx = -1
    for (let i = 0; i < sections.length; i++) {
      const s = sections[i]!
      if (s.type !== "image") continue
      const nums = getCells(s)
        .map((c) => extractSliceNumber(c.src || ""))
        .filter((x): x is number => x != null)
      const min = nums.length ? Math.min(...nums) : Number.POSITIVE_INFINITY
      if (min > n) return i
      afterIdx = i
    }
    return afterIdx >= 0 ? afterIdx + 1 : sections.length
  }

  // Insert a cell into a split band ordered by cell letter (a, b, c → left to right).
  function insertCellByLetter(cells: EmailImageT[], newCell: EmailImageT, letter: string) {
    let at = cells.length
    for (let i = 0; i < cells.length; i++) {
      const m = SLICE_SRC_REGEX.exec(cells[i]!.src || "")
      const existingLetter = m ? (m[2] || "") : ""
      if (existingLetter > letter) {
        at = i
        break
      }
    }
    cells.splice(at, 0, newCell)
  }

  /**
   * Pull any detected-but-unplaced slices into the email. Each slice NUMBER is its own image
   * band (a row); slices that only differ by letter (from a vertical split, e.g. 04_a/04_b)
   * join that number's band as extra cells (columns). Placing them all as cells of one band —
   * the old behaviour — produced a row of many columns.
   */
  function applyMissing(email: EmailT): { added: Array<{ key: string; sectionIndex: number }> } {
    const added: Array<{ key: string; sectionIndex: number }> = []

    // Normalize legacy sections to the typed shape in place (lazy migration).
    email.sections = normalizeSections(email.sections)

    // Sorted by number then letter, so bands land in order and letters read left-to-right.
    const missing = missingSlices(email)
    if (missing.length === 0) return { added }

    for (const slice of missing) {
      const num = String(slice.number).padStart(2, "0")
      const suffix = slice.letter ? `_${slice.letter}` : ""
      const newCell: EmailImageT = { src: `$SIZE_${num}${suffix}.${slice.ext}` }

      // If a band already holds this slice number, the new slice is a vertical-split sibling
      // → add it as a cell. Otherwise it's a new row → give it its own band.
      const bandIdx = email.sections.findIndex(
        (s) => s.type === "image" && getCells(s).some((c) => extractSliceNumber(c.src || "") === slice.number),
      )
      if (bandIdx >= 0) {
        const cells = getCells(email.sections[bandIdx]!)
        insertCellByLetter(cells, newCell, slice.letter)
        setCells(email.sections[bandIdx]!, cells)
        added.push({ key: slice.key, sectionIndex: bandIdx })
      } else {
        const insertAt = imageBandInsertIndex(email.sections, slice.number)
        email.sections.splice(insertAt, 0, { type: "image", cells: [newCell] })
        added.push({ key: slice.key, sectionIndex: insertAt })
      }
    }

    return { added }
  }

  return {
    availableSlices,
    sizeCounts,
    sliceVersions,
    sliceVersion,
    loading,
    loaded,
    lastError,
    fetchSlices,
    missingSlices,
    missingSliceNumbers,
    getExistingSliceKeys,
    applyMissing,
  }
}
